<script setup lang="ts">
import { computed, nextTick, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useVuelidate } from '@vuelidate/core'
import { helpers } from '@vuelidate/validators'
import { useUmsokn } from '@/composables/Applications/UseUmsokn'
import { isValidKennitala } from '@/assets/Utils/kennitala'
import SsnInput from '../../Shared/SSNInput.vue'

interface Child {
  name: string
  ssn: string
}

const { t } = useI18n()
const { umsokn, onLoaded } = useUmsokn()

const props = defineProps<{ disable?: boolean }>()

onLoaded(async () => {
  if (!Array.isArray(umsokn.value.children) || umsokn.value.children.length === 0) {
    umsokn.value.children = [{ name: '', ssn: '' }] as Child[]
  }

  // Wait until computed rules and validation tree rebuild
  await nextTick()
  v$.value.$reset()
})


const rules = computed(() => ({
  umsokn: {
    children: Array.isArray(umsokn.value?.children)
      ? umsokn.value.children.map(() => ({
          // Name optional, no required
          name: {},
        }))
      : []
  }
}))
const v$ = useVuelidate(rules, { umsokn })

function addChild() {
  umsokn.value?.children.push({ name: '', ssn: '' })
}

function removeChild(i: number) {
  umsokn.value?.children.splice(i, 1)
  if (umsokn.value.children.length === 0) {
    umsokn.value.children.push({ name: '', ssn: '' })
  }
}

defineExpose({
  async validate() {
    // touch all first to update the UI
    if (Array.isArray(umsokn.value.children)) {
      umsokn.value.children.forEach((_: Child, i: number) => {
        v$.value.umsokn.children[i].$touch()
      })
    }

    const isValid = await v$.value.$validate()
    return !v$.value.$invalid && isValid
  }
})
</script>

<template>
  <div v-if="umsokn">
    <v-col>
      <div class="forms__header">
        {{ t('makalifeyrir_umsokn_step5_header') }}
      </div>

      <v-row>
        <v-col cols="12">
          <p class="forms__paragraph">
            {{ t('makalifeyrir_umsokn_step5_text') }}
          </p>
        </v-col>
      </v-row>

      <transition-group name="list">
        <div v-for="(child, i) in umsokn.children" :key="'child_' + i">
            <div v-if="i > 0" class="forms__divider__container">
              <div class="forms__divider"></div>
              <button
                v-if="!props.disable"
                type="button"
                class="forms__warning__list__wrapper"
                @click="removeChild(i)"
              >
                <img class="forms__warning__icon" src="@/assets/icons/Close.svg" alt="Remove" />
              </button>
          </div>


          <v-row>
            <!-- Name -->
          <v-col cols="12" md="6">
            <v-text-field
              v-model="child.name"
              :readonly="props.disable"
              :label="t('makalifeyrir_umsokn_step5_name')"
              variant="outlined"
              density="comfortable"
              :error="v$.umsokn.children[i]?.name?.$error"
            />
          </v-col>

          <!-- SSN -->
          <v-col cols="12" md="6">
            <SsnInput
              v-model="child.ssn"
              :readonly="props.disable"
              :label="t('makalifeyrir_umsokn_step5_ssn')"
              placeholder="######-####"
              variant="outlined"
              :error="v$.umsokn.children[i]?.ssn?.$error"
              :error-messages="[
                v$.umsokn.children[i]?.ssn?.optionalKennitala?.$invalid
                  ? t('lan_umsokn_step2_kennitala_invalid')
                  : ''
              ].filter(Boolean)"
            />
          </v-col>
          </v-row>
        </div>
      </transition-group>

      <div class="m-t-40" v-if="!props.disable">
        <v-btn
          color="primary"
          variant="outlined"
          prepend-icon="mdi-plus"
          class="mt-4"
          @click="addChild"
        >
          {{ $t('ororku_umsokn_step3_add') }}
        </v-btn>
      </div>

      <v-row>
        <v-col cols="12" style="margin-top: 40px;">
          <p class="forms__paragraph">
            {{ t('makalifeyrir_umsokn_step5_disclaimer_text') }}
          </p>
        </v-col>
      </v-row>
    </v-col>
  </div>
</template>

<style scoped lang="scss">

</style>
